// Single source of truth for the impersonation token in browser storage.
// All reads/writes go through this module so logout, exit, and the axios
// interceptor agree on key names. Keys live in sessionStorage (cleared on
// tab close) so a forgotten impersonation session can't outlive the tab.

const TOKEN_KEY = 'impersonation_token';
const ID_KEY    = 'impersonation_id';
const NAME_KEY  = 'impersonation_client_name';

export function getImpersonationToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getImpersonationId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(ID_KEY);
  } catch {
    return null;
  }
}

export function getImpersonationClientName(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

export type ImpersonationKind = 'partner' | 'support';

interface ImpersonationClaims {
  impersonationKind?: ImpersonationKind;
}

export function readImpersonationKind(token: string): ImpersonationKind | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as ImpersonationClaims;
    if (decoded.impersonationKind === 'support') return 'support';
    if (decoded.impersonationKind === 'partner') return 'partner';
    return null;
  } catch {
    return null;
  }
}

export function isPartnerImpersonation(): boolean {
  const token = getImpersonationToken();
  if (!token) return false;
  return readImpersonationKind(token) !== 'support';
}

export function setImpersonation(input: {
  token: string;
  impersonationId: string;
  clientName: string;
}): void {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, input.token);
    window.sessionStorage.setItem(ID_KEY, input.impersonationId);
    window.sessionStorage.setItem(NAME_KEY, input.clientName);
  } catch {
    // Quota exceeded / privacy mode — fail silently. The /impersonate request
    // succeeded server-side so the audit log is correct; the user just won't
    // see a usable session. Ideally surface a toast at the call site.
  }
}

export function clearImpersonation(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(ID_KEY);
    window.sessionStorage.removeItem(NAME_KEY);
  } catch {
    // Same rationale as setImpersonation.
  }
}
